import {
  type Environment,
  type AuthState,
  type TabContext,
  type BridgeError,
  BRIDGE_ERROR_CODES,
  type BridgeWarning,
  BRIDGE_WARNING_CODES,
  type StateStamp,
  type StateRevisions,
  type CaraDestination,
  type SmartFillUiPreferences
} from '../dtos';
import {
  objectAt,
  enumAt,
  exactObject,
  identifierAt,
  hasOwn,
  nullableAt,
  nonBlankTextAt,
  literalAt,
  webOriginAt,
  textAt,
  MAX_MESSAGE_TEXT,
  objectParser,
  oneOf,
  nonBlankText,
  booleanAt,
  integerAt
} from './primitives';
import { type OpenCaraPayload, type SettingsUpdatePayload } from '../protocol';

export function parseEnvironment(value: unknown, path = '$'): Environment {
  const object = objectAt(value, path);
  const kind = enumAt(object.kind, ['production', 'staging', 'dev'] as const, `${path}.kind`);
  if (kind === 'production' || kind === 'staging') {
    exactObject(value, path, ['kind']);
  } else {
    const dev = exactObject(value, path, ['kind', 'user'], ['worktree']);
    identifierAt(dev.user, `${path}.user`);
    if (hasOwn(dev, 'worktree')) identifierAt(dev.worktree, `${path}.worktree`);
  }
  return value as Environment;
}

export function parseAuthState(value: unknown, path = '$'): AuthState {
  const object = exactObject(value, path, ['status', 'userDisplayName', 'agencyName']);
  const status = enumAt(
    object.status,
    ['checking', 'signed-in', 'signed-out', 'expired'] as const,
    `${path}.status`
  );
  if (status === 'signed-in') {
    nullableAt(object.userDisplayName, `${path}.userDisplayName`, nonBlankTextAt);
    nullableAt(object.agencyName, `${path}.agencyName`, nonBlankTextAt);
  } else {
    literalAt(object.userDisplayName, null, `${path}.userDisplayName`);
    literalAt(object.agencyName, null, `${path}.agencyName`);
  }
  return value as AuthState;
}

export function parseTabContext(value: unknown, path = '$'): TabContext {
  const object = exactObject(value, path, ['status', 'contextId', 'origin', 'title']);
  const status = enumAt(
    object.status,
    ['unavailable', 'unsupported', 'ready'] as const,
    `${path}.status`
  );
  if (status === 'unavailable') {
    literalAt(object.contextId, null, `${path}.contextId`);
    literalAt(object.origin, null, `${path}.origin`);
    literalAt(object.title, null, `${path}.title`);
  } else if (status === 'unsupported') {
    identifierAt(object.contextId, `${path}.contextId`);
    nullableAt(object.origin, `${path}.origin`, webOriginAt);
    nullableAt(object.title, `${path}.title`, (item, itemPath) =>
      textAt(item, itemPath, MAX_MESSAGE_TEXT)
    );
  } else {
    identifierAt(object.contextId, `${path}.contextId`);
    webOriginAt(object.origin, `${path}.origin`);
    textAt(object.title, `${path}.title`, MAX_MESSAGE_TEXT);
  }
  return value as TabContext;
}

export const parseBridgeError = objectParser<BridgeError>({
  code: oneOf(BRIDGE_ERROR_CODES),
  message: nonBlankText(MAX_MESSAGE_TEXT),
  retryable: booleanAt
});

export const parseBridgeWarning = objectParser<BridgeWarning>({
  code: oneOf(BRIDGE_WARNING_CODES),
  message: nonBlankText(MAX_MESSAGE_TEXT)
});

export const parseStateStamp = objectParser<StateStamp>({
  epoch: identifierAt,
  revision: integerAt
});
export const parseStateRevisions = objectParser<StateRevisions>({
  epoch: identifierAt,
  auth: integerAt,
  tab: integerAt,
  activeBucket: integerAt,
  pageSession: integerAt,
  superCopy: integerAt
});

export function parseCaraDestination(value: unknown, path = '$'): CaraDestination {
  const object = objectAt(value, path);
  const kind = enumAt(
    object.kind,
    ['HOME', 'SMART_FILL', 'BUCKET', 'WORKSPACE'] as const,
    `${path}.kind`
  );
  if (kind === 'HOME' || kind === 'SMART_FILL') {
    exactObject(value, path, ['kind']);
  } else if (kind === 'BUCKET') {
    const destination = exactObject(value, path, ['kind', 'bucketId']);
    identifierAt(destination.bucketId, `${path}.bucketId`);
  } else {
    const destination = exactObject(value, path, ['kind', 'workspaceId']);
    identifierAt(destination.workspaceId, `${path}.workspaceId`);
  }
  return value as CaraDestination;
}

export const openCaraPayloadAt = objectParser<OpenCaraPayload>({
  destination: parseCaraDestination
});

export const parseSmartFillUiPreferences = objectParser<SmartFillUiPreferences>(
  { showPageButtons: booleanAt, showBottomBar: booleanAt },
  { optional: { showTechnicalProblems: booleanAt } }
);

export const settingsUpdatePayloadAt = objectParser<SettingsUpdatePayload>(
  {},
  {
    optional: {
      showPageButtons: booleanAt,
      showBottomBar: booleanAt,
      showTechnicalProblems: booleanAt
    }
  }
);
